import {
    Box,
    CircularProgress,
} from '@mui/material';
import type {
    GridApi,
    IFilterModel,
    IFooter,
    IGridDataEntry,
    IGridOptions,
    ISortModel,
    PageChangeEvent,
    Subscription,
} from '@uipath/apollo-angular-elements';
import { isEqual } from 'lodash';
import React, {
    Children,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import type {
    IApDataGridColumnProps,
    IColumnWithRender,
} from './ApDataGridColumn';
import type {
    IApDataGridHeaderButtonProps,
    IApDataGridHeaderProps,
} from './ApDataGridHeader';
import type { IApDataGridRowProps } from './ApDataGridRowButton';
import { getFilteredData } from './utils';

enum ChildType {
    ApDataGridColumn = 'ApDataGridColumn',
    ApDataGridFooter = 'ApDataGridFooter',
    ApDataGridRowActions = 'ApDataGridRowActions',
    ApDataGridRowButton = 'ApDataGridRowButton',
    ApDataGridHeader = 'ApDataGridHeader',
    ApDataGridHeaderButton = 'ApDataGridHeaderButton',
    ApDataGridColumnSearchFilter = 'ApDataGridColumnSearchFilter',
    ApDataGridColumnDropdownFilter = 'ApDataGridColumnDropdownFilter',
}

interface IChildProps<T> {
    [ChildType.ApDataGridColumn]?: Array<IApDataGridColumnProps<T>>;
    [ChildType.ApDataGridFooter]?: IFooter;
    [ChildType.ApDataGridRowActions]?: Array<IApDataGridRowProps<T>>;
    [ChildType.ApDataGridHeader]?: IApDataGridHeaderProps<T>;
}

type IGridExcludedOptions = 'columns' | 'footerOptions' | 'gridRowButtons' | 'headerOptions' | 'expandRenderer' | 'data';

interface IGridFilteredOptions<T extends IGridDataEntry> extends Omit<IGridOptions<T>, IGridExcludedOptions> {}

export interface WithChildren {
    children?: Array<JSX.Element | JSX.Element[] | null | undefined | false> | JSX.Element | null | false;
}

interface IGridFilters<T> {
    filter?: Array<IFilterModel<T>>;
    sort?: Array<ISortModel<T>>;
    pageIndex?: number;
    pageSize?: number;
    searchTerm?: string;
}
interface IGridProps<T extends IGridDataEntry> extends IGridFilteredOptions<T> {
    /**
     * Children are used in order to pass some props through them and construct
     * the configuration object, instead of passing the whole object to <ap-data-grid>.
    */
    children: WithChildren['children'];
    /**
     * The data to be displayed in the grid.
    */
    data: T[];
    /**
     * If you get all of the data and it's not paginated, you can pass this prop
     * and the wrapper will handle page changes / filter changes / page size changes
     * and searches without having to handle all of this setup on the consumer side.
    */
    unpaginated?: boolean;
    /**
     * This is a function that returns a react element to be displayed on an expanded
     * row.
    */
    expandRenderer?: (row: T) => JSX.Element;
    /**
     * This is a method that gets called whenever the grid has been interacted with (
     * either a page change, filter applied, search, etc). It will provide all of the
     * grid state so you can make the request for new data and update it.
    */
    onChange?: ({
        filter,
        sort,
        pageIndex,
        pageSize,
        searchTerm,
    }: IGridFilters<T>) => void;
    /**
     * An initial set of filters for the grid (maybe custom initial sorting,
     * filters applied, etc).
    */
    initialFilters?: IGridFilters<T>;
    dataCy?: string;
    /**
     * The underlying `ap-data-grid` stencil component already handles theming,
     * but you can also override it if needed (tests / storybooks / iframe etc).
    */
    theme?: 'light-hc' | 'light' | 'dark';
    /**
     * The underlying `ap-data-grid` stencil component already handles language,
     * but you can also override it if needed (tests / storybooks / iframe etc).
    */
    language?: string;
}

// Separator used on the slots in `ui-grid-new` component from @uipath/apollo-angular-elements-private
const SLOT_SEPARATOR = '__slot__';

enum SlotType {
    Cell = 'cell',
    RowButton = 'rowButton',
    Header = 'header',
    Expanded = 'expanded',
    CardView = 'cardView',
}

export const ApDataGridWrapper = <T extends IGridDataEntry>(props: IGridProps<T>) => {
    const apGridRef = useRef<any>(null);

    // Refs to store the subscriptions
    const sortSubscriptionRef = useRef<Subscription | null>(null);
    const filterSubscriptionRef = useRef<Subscription | null>(null);

    // Loading state and displayed data
    const [ loading, setLoading ] = useState<boolean>(true);
    const [ displayedData, setDisplayedData ] = useState<T[]>();

    const [ slots, setSlots ] = useState<string[]>();
    const [ slotNodes, setSlotNodes ] = useState<Array<JSX.Element | undefined>>();

    // filters for grid
    const [ searchTerm, setSearchTerm ] = useState<string>(props.initialFilters?.searchTerm ?? '');
    const [ sortOpts, setSortOpts ] = useState<Array<ISortModel<T>>>(props.initialFilters?.sort ?? []);
    const [ filterOpts, setFilterOpts ] = useState<Array<IFilterModel<T>>>(props.initialFilters?.filter ?? []);
    const [ sortFunction, setSortFunction ] = useState<((a: T, b: T) => number)>();

    // footer props
    const [ footerProps, setFooterProps ] = useState<IChildProps<T>[ChildType.ApDataGridFooter]>();

    // header, column and action buttons
    const [ headerProps, setHeaderProps ] = useState<IChildProps<T>[ChildType.ApDataGridHeader]>();
    const [ columnProps, setColumnProps ] = useState<IChildProps<T>[ChildType.ApDataGridColumn]>();
    const [ rowButtonProps, setRowButtonProps ] = useState<IChildProps<T>[ChildType.ApDataGridRowActions]>();

    const {
        children: apGridChildren,
        expandRenderer,
        unpaginated,
        data,
        onChange,
        onGridApi,
        theme,
        language,
        ...apGridOtherProps
    } = props;

    // This will be used for unpaginated page calculations
    const calculateData = useCallback(() => {
        const {
            pageSize, pageIndex,
        } = footerProps ?? {};

        if (pageIndex !== undefined && pageSize) {
            let finalData = [ ...data ];

            if (sortFunction) {
                finalData = finalData.sort(sortFunction);
            }

            if ((searchTerm || filterOpts.length) && columnProps) {
                finalData = getFilteredData({
                    data: finalData,
                    search: searchTerm,
                    filters: filterOpts,
                    columns: columnProps,
                });
            }

            const newData = finalData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
            // prevent setting data if it's the same (it loses selection state)
            if (!isEqual(newData, displayedData)) {
                setDisplayedData(newData);

                if (footerProps && footerProps.length !== finalData.length) {
                    setFooterProps(prev => ({
                        ...prev,
                        length: finalData.length,
                    }));
                }
            }

            setLoading(false);
        }
    }, [ footerProps, data, sortFunction, searchTerm, columnProps, filterOpts, displayedData ]);

    // cleanup filter & sort subscriptions if they exist
    useEffect(() => () => {
        sortSubscriptionRef.current?.unsubscribe();
        filterSubscriptionRef.current?.unsubscribe();
    }, []);

    // Calculate displayed data based on dependencies
    useEffect(() => {
        if (unpaginated) {
            calculateData();
        } else if (data && !isEqual(data, displayedData)) {
            setDisplayedData(data);
            setLoading(false);
        }
    }, [ calculateData, unpaginated, footerProps, data ]);

    // Call onChange custom method whenever data changes so that the consumer can re-calculate his data.
    useEffect(() => {
        onChange?.({
            filter: filterOpts,
            sort: sortOpts,
            pageIndex: footerProps?.pageIndex,
            pageSize: footerProps?.pageSize,
            searchTerm: searchTerm ?? '',
        });
    }, [
        filterOpts,
        sortOpts,
        searchTerm,
        footerProps?.pageIndex,
        footerProps?.pageSize,
        onChange,
    ]);

    // Determine props from children components
    useEffect(() => {
        if (!props.children) {
            return;
        }

        const childProps: IChildProps<T> = {};
        Children.forEach(props.children, (child) => {
            if (child && !Array.isArray(child)) {
                const {
                    children, render, ...otherProps
                } = child.props;

                const sort = sortOpts?.find(sortOpt =>
                    sortOpt.field === otherProps.property,
                )?.direction ?? otherProps.sort ?? apGridRef.current?.configuration?.sort;

                switch (child.type.displayName) {
                    case ChildType.ApDataGridColumn:
                        if (!childProps[ChildType.ApDataGridColumn]) {
                            childProps[ChildType.ApDataGridColumn] = [];
                        }
                        childProps[ChildType.ApDataGridColumn]?.push({
                            ...otherProps,
                            ...Children
                                .toArray(children)
                                .reduce((acc: any, grandChild: any) => {
                                    if (grandChild.type.displayName === ChildType.ApDataGridColumnSearchFilter) {
                                        acc.searchFilterOpts = { ...grandChild.props };
                                    } else if (grandChild.type.displayName === ChildType.ApDataGridColumnDropdownFilter) {
                                        acc.dropdownFilterOpts = { ...grandChild.props };
                                    }
                                    return acc;
                                }, {}),
                            ...render ? {
                                render,
                                isCustom: true,
                            } : {},
                            ...sort ? { sort } : {},
                        });
                        break;

                    case ChildType.ApDataGridFooter:
                        childProps[ChildType.ApDataGridFooter] = {
                            ...otherProps,
                            pageSize: otherProps.pageSize ?? footerProps?.pageSize ?? 10,
                            pageIndex: otherProps.pageIndex ?? footerProps?.pageIndex ?? props.initialFilters?.pageIndex ?? 0,
                        };
                        break;

                    case ChildType.ApDataGridRowActions:
                        childProps[ChildType.ApDataGridRowActions] = Children.map(children, (grandChild) => {
                            if (!grandChild) {
                                return {};
                            }

                            if (grandChild.type.displayName !== ChildType.ApDataGridRowButton) {
                                throw new Error(`Invalid child of ${ChildType.ApDataGridRowActions} type: ${grandChild.type.displayName}`);
                            }

                            return {
                                ...grandChild.props,
                                ...grandChild.props.render ? {
                                    render: grandChild.props.render,
                                    isCustom: true,
                                } : {},
                            };
                        });
                        break;

                    case ChildType.ApDataGridHeader:
                        childProps[ChildType.ApDataGridHeader] = {
                            ...otherProps,
                            gridHeaderButtons: Children.map(children, (grandChild) => {
                                if (grandChild.type.displayName !== ChildType.ApDataGridHeaderButton) {
                                    throw new Error(`Invalid child of ${ChildType.ApDataGridHeader} type: ${grandChild.type.displayName}`);
                                }
                                return {
                                    ...grandChild.props,
                                    ...grandChild.props.render ? {
                                        render: grandChild.props.render,
                                        isCustom: true,
                                    } : {},
                                };
                            }),
                        };
                        delete childProps[ChildType.ApDataGridHeader]?.children;
                        break;

                    case undefined:
                    case false:
                    case null:
                        break;

                    default:
                        throw new Error(`Invalid child type: ${child.type.displayName}`);
                }
            }
        });

        setFooterProps(prev => ({
            ...prev,
            ...childProps[ChildType.ApDataGridFooter],
        }));
        setHeaderProps(prev => ({
            ...prev,
            ...childProps[ChildType.ApDataGridHeader],
        }));
        setColumnProps(childProps[ChildType.ApDataGridColumn]);
        setRowButtonProps(childProps[ChildType.ApDataGridRowActions]);
        // We want to re-calculate only on children change
    }, [ props.children ]);

    // Set the sort function based on the sort options
    useEffect(() => {
        if (!unpaginated || !sortOpts?.length) {
            return;
        }

        setSortFunction(() => (prev: any, cur: any) => {
            for (const sortOption of sortOpts) {
                if (!sortOption?.direction || prev[sortOption.field] === cur[sortOption.field]) {
                    continue;
                }
                if (sortOption.direction === 'asc') {
                    return prev[sortOption.field] > cur[sortOption.field] ? 1 : -1;
                }
                return prev[sortOption.field] < cur[sortOption.field] ? 1 : -1;
            }

            return 0;
        });
    }, [ unpaginated, sortOpts ]);

    const gridApi = useCallback((api: GridApi<T>) => {
        if (sortSubscriptionRef.current) {
            sortSubscriptionRef.current.unsubscribe();
        }

        if (filterSubscriptionRef.current) {
            filterSubscriptionRef.current.unsubscribe();
        }

        sortSubscriptionRef.current = api.sortManager.sort$.subscribe((sort) => {
            if (Object.keys(sort).length === 0) {
                return;
            }

            setSortOpts(prev => {
                if (!prev) {
                    return [ sort ];
                }

                const hasSort = prev.find(s => s.field === sort.field);

                // If it's already there, move it to the front
                if (hasSort) {
                    const index = prev.findIndex(s => s.field === sort.field);
                    prev.splice(index, 1);
                }

                return [ sort, ...prev ];
            });
        });

        let skippedFirst = false;

        filterSubscriptionRef.current = api.filterManager.filter$.subscribe((filter) => {
            // we don't have acess to rx so we can't do pipe(skip(1))
            if (!skippedFirst) {
                skippedFirst = true;
                return;
            }

            setFilterOpts(filter);
        });

        onGridApi?.(api);
    }, [ onGridApi ]);

    // listen for slots changed event and update the slots state
    useEffect(() => {
        if (apGridRef?.current) {
            const handleSlotsChanged = (event: CustomEvent) => {
                setSlots((prev) => Array.from(new Set([ ...prev ?? [], ...event.detail ])));
            };

            apGridRef.current.addEventListener('slotsChanged', handleSlotsChanged);

            return () => {
                apGridRef?.current?.removeEventListener('slotsChanged', handleSlotsChanged);
            };
        }
    }, [ apGridRef ]);

    // calculate the configuration object based on child props and component props.
    useEffect(() => {
        if (apGridRef.current) {
            const {
                render: _, ...restHeaderProps
            } = (headerProps ?? {}) as IApDataGridHeaderButtonProps<T>;

            apGridRef.current.configuration = {
                ...apGridOtherProps,
                data: displayedData ?? [],
                columns: columnProps?.map((column: IColumnWithRender<T>) => {
                    const {
                        render, ...finalColumn
                    } = column;

                    if (filterOpts) {
                        const filter = filterOpts.find(f => f.property === finalColumn.property);

                        if (!filter) {
                            return finalColumn;
                        }

                        if (finalColumn.searchFilterOpts) {
                            finalColumn.searchFilterOpts.value = filter?.meta ?? undefined;
                        }

                        if (finalColumn.dropdownFilterOpts) {
                            const matching = finalColumn.dropdownFilterOpts.items?.find(item => item.value === filter.value);

                            finalColumn.dropdownFilterOpts.value = matching;
                        }
                    }

                    return finalColumn;
                }) ?? [],
                ...footerProps ? {
                    footerOptions: {
                        ...footerProps,
                        pageChange: (event: PageChangeEvent) => {
                            setFooterProps(prev => ({
                                ...prev,
                                pageIndex: event.pageSize === prev?.pageSize ? event.pageIndex : 0,
                                pageSize: event.pageSize,
                            }));

                            footerProps.pageChange?.(event);
                        },
                    },
                } : { },
                ...rowButtonProps ? {
                    gridRowButtons: rowButtonProps.map((rowButtonProp: IApDataGridRowProps<T>) => {
                        const {
                            render, ...rest
                        } = rowButtonProp;

                        return rest;
                    }),
                } : { },
                ...headerProps ? {
                    headerOptions: {
                        ...restHeaderProps,
                        searchTerm: (term) => {
                            headerProps.searchTerm?.(term);

                            setSearchTerm(term);
                        },
                    },
                } : { },
                ...expandRenderer ? { expandRenderer } : { },
                onGridApi: gridApi,
            } as IGridOptions<T>;
        }
    }, [
        filterOpts,
        displayedData,
        columnProps,
        footerProps,
        rowButtonProps,
        headerProps,
        expandRenderer,
        apGridOtherProps,
        apGridRef,
        unpaginated,
        gridApi,
    ]);

    // Display the slots based on the slots state
    const displaySlots = useCallback(() => slots?.map(slot => {
        // prevent failure on ap-shell missmatch 
        if (typeof slot !== 'string') {
            return;
        }

        const type = slot?.split(SLOT_SEPARATOR)[0] as SlotType;

        if (!type) {
            return;
        }

        if (type === SlotType.Cell) {
            const [ , property, rowId ] = slot.split(SLOT_SEPARATOR);
            const found = displayedData?.[+rowId];

            return (
                <div
                    key={slot}
                    slot={slot}>
                    { found && (
                        columnProps?.find(c => c.property === property)?.render?.(found)
                    )}
                </div>
            );
        }

        if (type === SlotType.RowButton) {
            const [ , buttonId, rowId ] = slot.split(SLOT_SEPARATOR);
            const found = displayedData?.[+rowId];

            return (
                <div
                    key={slot}
                    slot={slot}>
                    { found && (
                        rowButtonProps?.find(c => c.id === buttonId)?.render?.(displayedData![+rowId], +rowId!)
                    )}
                </div>
            );
        }

        if (type === SlotType.Header) {
            const buttonId = slot.split(SLOT_SEPARATOR)[1];

            return (
                <div
                    key={slot}
                    slot={slot}>
                    { headerProps?.gridHeaderButtons?.find(h => h.id === buttonId)?.render?.() }
                </div>
            );
        }

        if (type === SlotType.Expanded) {
            const entryId = slot.split(SLOT_SEPARATOR)[1];
            const found = displayedData?.find(entry => String(entry.id) === String(entryId));

            return (
                <div
                    key={slot}
                    slot={slot}>
                    { found && expandRenderer?.(found) }
                </div>
            );
        }
    }), [ columnProps, displayedData, expandRenderer, headerProps?.gridHeaderButtons, rowButtonProps, slots ]);

    useEffect(() => {
        setSlotNodes(displaySlots());
    }, [ slots, columnProps, rowButtonProps, headerProps?.gridHeaderButtons, expandRenderer ]);

    return <>
        { loading && (
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <CircularProgress />
            </Box>
        )}
        <ap-data-grid
            data-cy={apGridOtherProps.dataCy}
            use-new-grid
            language={language}
            theme={theme}
            ref={apGridRef}
        >
            { slotNodes }
        </ap-data-grid>
    </>;
};
